import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';
import { AppConfig } from '@env/app-config';
import { SUPPRESS_HTTP_ERRORS } from '@interceptors/error.interceptor';
import { PagedResponse } from '@models/common/paged-response.model';
import {
  StorefrontQuote,
  CustomerAuthResponse,
  CustomerRegisterPendingResponse,
  StorefrontBlogPostDetail,
  StorefrontBlogCommentSubmission,
  StorefrontBlogPostSummary,
  StorefrontCart,
  StorefrontCategorySummary,
  StorefrontCheckoutResult,
  StorefrontCollectionDetail,
  StorefrontCollectionSummary,
  StorefrontCustomerAddress,
  StorefrontCustomerProfile,
  StorefrontHomeBanners,
  StorefrontLegalPolicy,
  StorefrontLegalPolicySummary,
  StorefrontLoyalty,
  StorefrontReferral,
  StorefrontLoyaltyTransaction,
  StorefrontMenuItem,
  StorefrontNotificationPreferences,
  StorefrontOrderDetail,
  StorefrontOrderReturn,
  StorefrontOrderSummary,
  StorefrontReturnRequest,
  StorefrontContactMessage,
  StorefrontPageDetail,
  StorefrontPaymentMethod,
  StorefrontProductDetail,
  StorefrontProductSummary,
  StorefrontShippingQuote,
  StorefrontDiscountPreview,
  StorefrontCheckoutSummary,
  StorefrontActivePromo,
  StorefrontSocial,
  StorefrontShopPrefs,
  StorefrontChatbotAskResponse,
  StorefrontChatbotHistoryMessage,
  StorefrontChatbotWidget,
  StorefrontWhatsAppContact,
  StorefrontPickupLocation,
  StorefrontTheme,
  StorefrontTenant,
  StorefrontWishlistItem,
  StorefrontMarket,
} from '../models/storefront.model';
import { StorefrontSessionService } from './storefront-session.service';
import { StorefrontCartStateService } from './storefront-cart-state.service';
import { StorefrontContextService } from './storefront-context.service';
import { StorefrontLocaleService } from './storefront-locale.service';

@Injectable({ providedIn: 'root' })
export class StorefrontApiService {
  private readonly http    = inject(HttpClient);
  private readonly session = inject(StorefrontSessionService);
  private readonly cartState = inject(StorefrontCartStateService);
  private readonly ctx     = inject(StorefrontContextService);
  private readonly locale  = inject(StorefrontLocaleService);
  private readonly base    = `${AppConfig.apiUrl}/api/storefront`;

  private withMarket(params: HttpParams): HttpParams {
    const market = this.ctx.marketCode();
    return market ? params.set('market', market) : params;
  }

  private withLang(params: HttpParams): HttpParams {
    const lang = this.locale.lang();
    return lang ? params.set('lang', lang) : params;
  }

  private withCatalogContext(params: HttpParams): HttpParams {
    return this.withLang(this.withMarket(params));
  }

  listTenants(): Observable<StorefrontTenant[]> {
    return this.http.get<StorefrontTenant[]>(`${this.base}/tenants`);
  }

  resolveTenant(storeSlug: string): Observable<StorefrontTenant> {
    return this.http.get<StorefrontTenant>(`${this.base}/tenants/${encodeURIComponent(storeSlug)}`);
  }

  resolveLegacyTenant(companyId: number): Observable<StorefrontTenant> {
    return this.http.get<StorefrontTenant>(`${this.base}/tenants/legacy/${companyId}`);
  }

  listProducts(
    companyId: number,
    page = 0,
    size = 24,
    q?: string,
    sort?: string,
    minPrice?: number,
    maxPrice?: number,
    collectionIds?: number[],
    categoryIds?: number[],
  ): Observable<PagedResponse<StorefrontProductSummary>> {
    let params = new HttpParams().set('companyId', String(companyId)).set('page', String(page)).set('size', String(size));
    if (q?.trim()) params = params.set('q', q.trim());
    if (sort) params = params.set('sort', sort);
    if (minPrice != null) params = params.set('minPrice', String(minPrice));
    if (maxPrice != null) params = params.set('maxPrice', String(maxPrice));
    for (const id of collectionIds ?? []) params = params.append('collectionIds', String(id));
    for (const id of categoryIds ?? []) params = params.append('categoryIds', String(id));
    params = this.withCatalogContext(params);
    return this.http.get<PagedResponse<StorefrontProductSummary>>(`${this.base}/catalog/products`, { params });
  }

  listMarkets(companyId: number): Observable<StorefrontMarket[]> {
    return this.http.get<StorefrontMarket[]>(`${this.base}/markets`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  listCategories(companyId: number): Observable<StorefrontCategorySummary[]> {
    return this.http.get<StorefrontCategorySummary[]>(`${this.base}/catalog/categories`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  /** Cotización pública por el token del correo; los errores (vencida, inexistente) los muestra la página. */
  getQuote(token: string): Observable<StorefrontQuote> {
    return this.http.get<StorefrontQuote>(`${this.base}/quotes/${encodeURIComponent(token)}`, {
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  getProduct(companyId: number, slug: string): Observable<StorefrontProductDetail> {
    return this.http.get<StorefrontProductDetail>(`${this.base}/catalog/products/${slug}`, {
      params: this.withCatalogContext(new HttpParams().set('companyId', String(companyId))),
    });
  }

  listCollections(companyId: number, page = 0, size = 20): Observable<PagedResponse<StorefrontCollectionSummary>> {
    return this.http.get<PagedResponse<StorefrontCollectionSummary>>(`${this.base}/catalog/collections`, {
      params: new HttpParams().set('companyId', String(companyId)).set('page', String(page)).set('size', String(size)),
    });
  }

  getCollection(companyId: number, slug: string): Observable<StorefrontCollectionDetail> {
    return this.http.get<StorefrontCollectionDetail>(`${this.base}/catalog/collections/${slug}`, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  /** RF-11: resuelve redirección SEO (p. ej. colección eliminada). */
  resolveSeoRedirect(companyId: number, fromPath: string): Observable<{ fromPath: string; toPath: string; redirectType: number } | null> {
    return this.http.get<{ fromPath: string; toPath: string; redirectType: number }>(`${this.base}/seo/redirect`, {
      params: new HttpParams().set('companyId', String(companyId)).set('fromPath', fromPath),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    }).pipe(
      catchError(() => of(null)),
    );
  }

  getCollectionProducts(companyId: number, slug: string, page = 0, size = 24): Observable<PagedResponse<StorefrontProductSummary>> {
    return this.http.get<PagedResponse<StorefrontProductSummary>>(`${this.base}/catalog/collections/${slug}/products`, {
      params: this.withCatalogContext(
        new HttpParams().set('companyId', String(companyId)).set('page', String(page)).set('size', String(size)),
      ),
    });
  }

  getCart(companyId: number): Observable<StorefrontCart> {
    return this.http.get<StorefrontCart>(`${this.base}/cart`, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    }).pipe(tap(c => this.syncCart(companyId, c)));
  }

  addToCart(companyId: number, productVariantId: number, quantity: number): Observable<StorefrontCart> {
    const body: Record<string, unknown> = { productVariantId, quantity };
    const market = this.ctx.marketCode();
    if (market) body['marketCode'] = market;
    return this.http.post<StorefrontCart>(`${this.base}/cart/items`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    }).pipe(tap(c => {
      this.syncCart(companyId, c);
      const line = c.items?.find(item => item.productVariantId === productVariantId);
      if (line) {
        this.cartState.announceAdded({
          productName: line.productName,
          variantName: line.variantName,
          imageUrl: line.imageUrl ?? null,
          quantity,
        });
      }
    }));
  }

  updateCartItem(companyId: number, itemId: number, quantity: number): Observable<StorefrontCart> {
    return this.http.patch<StorefrontCart>(`${this.base}/cart/items/${itemId}`, { quantity }, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    }).pipe(tap(c => this.syncCart(companyId, c)));
  }

  removeCartItem(companyId: number, itemId: number): Observable<StorefrontCart> {
    return this.http.delete<StorefrontCart>(`${this.base}/cart/items/${itemId}`, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    }).pipe(tap(c => this.syncCart(companyId, c)));
  }

  /** El mercado decide si se ofrece la tarjeta de prueba (solo en mercados sandbox o si la tienda la activó). */
  paymentMethods(companyId: number, marketCode?: string | null): Observable<StorefrontPaymentMethod[]> {
    let params = new HttpParams().set('companyId', String(companyId));
    if (marketCode) params = params.set('marketCode', marketCode);
    return this.http.get<StorefrontPaymentMethod[]>(`${this.base}/payments/methods`, { params });
  }

  createMiCuentaWebFormToken(
    companyId: number,
    body: Record<string, unknown>,
  ): Observable<{
    formToken: string;
    publicKey: string;
    jsEndpoint: string;
    intentId: string;
    amount: number;
    currency: string;
  }> {
    return this.http.post<{
      formToken: string;
      publicKey: string;
      jsEndpoint: string;
      intentId: string;
      amount: number;
      currency: string;
    }>(`${this.base}/payments/micuentaweb/form-token`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    });
  }

  shippingQuote(
    companyId: number,
    cartSubtotal: number,
    options?: {
      ubigeoCode?: string;
      totalWeightGrams?: number | null;
      totalVolumeCm3?: number | null;
      /** Productos del carrito: los que no se envían a la zona quitan esa opción (RF-09). */
      productIds?: number[];
      fulfillmentType?: 'SHIP' | 'PICKUP';
    },
  ): Observable<StorefrontShippingQuote[]> {
    const body: Record<string, unknown> = {
      cartSubtotal,
      totalWeightGrams: options?.totalWeightGrams != null ? options.totalWeightGrams : 0,
      fulfillmentType: options?.fulfillmentType ?? 'SHIP',
    };
    if (options?.totalVolumeCm3) {
      body['totalVolumeCm3'] = options.totalVolumeCm3;
    }
    if (options?.productIds?.length) {
      body['productIds'] = options.productIds;
    }
    if (options?.ubigeoCode) {
      body['ubigeoCode'] = options.ubigeoCode;
    }
    return this.http.post<StorefrontShippingQuote[]>(`${this.base}/shipping/quote`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  pickupLocations(companyId: number): Observable<StorefrontPickupLocation[]> {
    return this.http.get<StorefrontPickupLocation[]>(`${this.base}/shipping/pickup-locations`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  shippingCoverage(companyId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/shipping/coverage`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  /** Importes que tendrá el pedido con lo elegido en el checkout (mismo cálculo que el pedido y el cobro). */
  checkoutSummary(companyId: number, body: Record<string, unknown>): Observable<StorefrontCheckoutSummary> {
    return this.http.post<StorefrontCheckoutSummary>(`${this.base}/checkout/summary`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    });
  }

  discountPreview(
    companyId: number,
    orderSubtotal: number,
    itemQuantity: number,
    couponCode?: string | null,
    lines?: { productId: number; lineTotal: number; quantity: number }[] | null,
  ): Observable<StorefrontDiscountPreview> {
    return this.http.post<StorefrontDiscountPreview>(`${this.base}/discounts/preview`, {
      orderSubtotal,
      itemQuantity,
      couponCode: couponCode?.trim() || null,
      lines: lines?.map(l => ({
        productId: l.productId,
        lineTotal: l.lineTotal,
        quantity: l.quantity,
      })) ?? null,
    }, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    });
  }

  activePromos(companyId: number): Observable<StorefrontActivePromo[]> {
    return this.http.get<StorefrontActivePromo[]>(`${this.base}/discounts/active`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  checkout(companyId: number, payload: Record<string, unknown>): Observable<StorefrontCheckoutResult> {
    const market = this.ctx.marketCode();
    const body = market ? { ...payload, marketCode: market } : payload;
    return this.http.post<StorefrontCheckoutResult>(`${this.base}/checkout`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
      headers: this.cartHeaders(companyId),
    });
  }

  register(companyId: number, data: {
    email: string; password: string; firstName?: string; lastName?: string; acceptsMarketing?: boolean; acceptTerms: boolean;
    referralCode?: string;
  }): Observable<CustomerRegisterPendingResponse> {
    return this.http.post<CustomerRegisterPendingResponse>(`${AppConfig.apiUrl}/api/storefront/customers/register`, data, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    }).pipe(tap(r => this.rememberPendingRegistration(companyId, r)));
  }

  /** Código del correo: crea la cuenta y deja la sesión iniciada (igual que un login). */
  verifyRegistration(companyId: number, registrationToken: string, code: string): Observable<CustomerAuthResponse> {
    return this.http.post<CustomerAuthResponse>(`${AppConfig.apiUrl}/api/storefront/customers/register/verify`,
      { registrationToken, code }, {
        params: new HttpParams().set('companyId', String(companyId)),
        context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
      }).pipe(
      tap(r => this.session.setAuth(companyId, r.accessToken, r.email)),
      tap(() => this.session.clearPendingRegistration(companyId)),
      tap(() => { this.mergeGuestCart(companyId).subscribe(); }),
    );
  }

  resendRegistrationCode(companyId: number, registrationToken: string): Observable<CustomerRegisterPendingResponse> {
    return this.http.post<CustomerRegisterPendingResponse>(`${AppConfig.apiUrl}/api/storefront/customers/register/resend`,
      { registrationToken }, {
        params: new HttpParams().set('companyId', String(companyId)),
        context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
      });
  }

  /** Retoma un registro sin verificar con el correo y la contraseña (desde el login o en otro navegador). */
  resumeRegistration(companyId: number, email: string, password: string): Observable<CustomerRegisterPendingResponse> {
    return this.http.post<CustomerRegisterPendingResponse>(`${AppConfig.apiUrl}/api/storefront/customers/register/resume`,
      { email, password }, {
        params: new HttpParams().set('companyId', String(companyId)),
        context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
      }).pipe(tap(r => this.rememberPendingRegistration(companyId, r)));
  }

  private rememberPendingRegistration(companyId: number, r: CustomerRegisterPendingResponse): void {
    if (r.registrationToken) {
      this.session.setPendingRegistration(companyId, { registrationToken: r.registrationToken, email: r.email });
    }
  }

  /** «Crear cuenta»: si el correo ya tiene cuenta en la tienda, para avisarlo antes de enviar el formulario. */
  checkEmail(companyId: number, email: string): Observable<{ registered: boolean }> {
    return this.http.post<{ registered: boolean }>(`${AppConfig.apiUrl}/api/storefront/customers/email-check`, { email }, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  login(companyId: number, email: string, password: string): Observable<CustomerAuthResponse> {
    return this.http.post<CustomerAuthResponse>(`${AppConfig.apiUrl}/api/storefront/customers/login`, { email, password }, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    }).pipe(
      tap(r => this.session.setAuth(companyId, r.accessToken, r.email)),
      tap(() => { this.mergeGuestCart(companyId).subscribe(); }),
    );
  }

  /** «¿Olvidaste tu contraseña?»: el backend responde igual exista o no la cuenta. */
  forgotPassword(companyId: number, email: string): Observable<void> {
    return this.http.post<void>(`${AppConfig.apiUrl}/api/storefront/customers/password/forgot`, { email }, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  /** Comprueba si el enlace del correo sigue vigente antes de pedir la nueva contraseña. */
  checkPasswordResetLink(companyId: number, token: string): Observable<void> {
    return this.http.post<void>(`${AppConfig.apiUrl}/api/storefront/customers/password/reset/check`, { token }, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  /** Guarda la nueva contraseña con el enlace y deja la sesión iniciada en este dispositivo. */
  resetPassword(companyId: number, token: string, password: string): Observable<CustomerAuthResponse> {
    return this.http.post<CustomerAuthResponse>(`${AppConfig.apiUrl}/api/storefront/customers/password/reset`, { token, password }, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    }).pipe(
      tap(r => this.session.setAuth(companyId, r.accessToken, r.email)),
      tap(() => { this.mergeGuestCart(companyId).subscribe(); }),
    );
  }

  /** «Cambiar contraseña»: el backend devuelve una sesión nueva (las demás se cierran). */
  changePassword(companyId: number, currentPassword: string, newPassword: string): Observable<CustomerAuthResponse> {
    return this.http.post<CustomerAuthResponse>(`${this.base}/account/password`, { currentPassword, newPassword }, {
      headers: this.authHeaders(companyId),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    }).pipe(tap(r => this.session.setAuth(companyId, r.accessToken, r.email)));
  }

  /** Fusiona carrito invitado (X-Cart-Session) con el carrito del cliente autenticado. */
  mergeGuestCart(companyId: number): Observable<StorefrontCart> {
    return this.getCart(companyId);
  }

  getProfile(companyId: number): Observable<StorefrontCustomerProfile> {
    return this.http.get<StorefrontCustomerProfile>(`${this.base}/account/profile`, {
      headers: this.authHeaders(companyId),
    });
  }

  getReferral(companyId: number): Observable<StorefrontReferral> {
    return this.http.get<StorefrontReferral>(`${this.base}/account/referral`, {
      headers: this.authHeaders(companyId),
    });
  }

  getLoyalty(companyId: number): Observable<StorefrontLoyalty> {
    return this.http.get<StorefrontLoyalty>(`${this.base}/account/loyalty`, {
      headers: this.authHeaders(companyId),
    });
  }

  getLoyaltyHistory(companyId: number, page: number, size: number): Observable<PagedResponse<StorefrontLoyaltyTransaction>> {
    return this.http.get<PagedResponse<StorefrontLoyaltyTransaction>>(`${this.base}/account/loyalty/history`, {
      headers: this.authHeaders(companyId),
      params: new HttpParams().set('page', page).set('size', size),
    });
  }

  updateProfile(
    companyId: number,
    data: { firstName?: string; lastName?: string; phone?: string },
  ): Observable<StorefrontCustomerProfile> {
    return this.http.patch<StorefrontCustomerProfile>(`${this.base}/account/profile`, data, {
      headers: this.authHeaders(companyId),
    });
  }

  getNotificationPreferences(companyId: number): Observable<StorefrontNotificationPreferences> {
    return this.http.get<StorefrontNotificationPreferences>(`${this.base}/account/notification-preferences`, {
      headers: this.authHeaders(companyId),
    });
  }

  updateNotificationPreference(
    companyId: number,
    data: { channel: string; category: string; enabled: boolean },
  ): Observable<StorefrontNotificationPreferences> {
    return this.http.put<StorefrontNotificationPreferences>(`${this.base}/account/notification-preferences`, data, {
      headers: this.authHeaders(companyId),
    });
  }

  listAddresses(companyId: number): Observable<StorefrontCustomerAddress[]> {
    return this.http.get<StorefrontCustomerAddress[]>(`${this.base}/account/addresses`, {
      headers: this.authHeaders(companyId),
    });
  }

  createAddress(companyId: number, data: Record<string, unknown>): Observable<StorefrontCustomerAddress> {
    return this.http.post<StorefrontCustomerAddress>(`${this.base}/account/addresses`, data, {
      headers: this.authHeaders(companyId),
    });
  }

  updateAddress(companyId: number, addressId: number, data: Record<string, unknown>): Observable<StorefrontCustomerAddress> {
    return this.http.put<StorefrontCustomerAddress>(`${this.base}/account/addresses/${addressId}`, data, {
      headers: this.authHeaders(companyId),
    });
  }

  deleteAddress(companyId: number, addressId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/account/addresses/${addressId}`, {
      headers: this.authHeaders(companyId),
    });
  }

  isInWishlist(companyId: number, productId: number): Observable<{ inWishlist: boolean }> {
    return this.http.get<{ inWishlist: boolean }>(`${this.base}/account/wishlist/contains/${productId}`, {
      headers: this.authHeaders(companyId),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  listWishlist(companyId: number): Observable<StorefrontWishlistItem[]> {
    return this.http.get<StorefrontWishlistItem[]>(`${this.base}/account/wishlist`, {
      headers: this.authHeaders(companyId),
    });
  }

  addToWishlist(companyId: number, productId: number, productVariantId?: number | null): Observable<StorefrontWishlistItem> {
    return this.http.post<StorefrontWishlistItem>(`${this.base}/account/wishlist/items`, {
      productId,
      productVariantId: productVariantId ?? null,
    }, {
      headers: this.authHeaders(companyId),
    });
  }

  removeFromWishlist(companyId: number, productId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/account/wishlist/items/${productId}`, {
      headers: this.authHeaders(companyId),
    });
  }

  /** Políticas publicadas (estándar y propias) para el pie de página. */
  getLegalPolicies(companyId: number): Observable<StorefrontLegalPolicySummary[]> {
    return this.http.get<StorefrontLegalPolicySummary[]>(`${this.base}/content/legal/policies`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  /** Política publicada por su dirección (/legal/{slug}). */
  getLegalPolicy(companyId: number, slug: string): Observable<StorefrontLegalPolicy> {
    return this.http.get<StorefrontLegalPolicy>(`${this.base}/content/legal/policies/${encodeURIComponent(slug)}`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getSocial(companyId: number): Observable<StorefrontSocial> {
    return this.http.get<StorefrontSocial>(`${this.base}/content/social`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getShopPrefs(companyId: number): Observable<StorefrontShopPrefs> {
    return this.http.get<StorefrontShopPrefs>(`${this.base}/content/shop-prefs`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getWhatsAppContact(companyId: number): Observable<StorefrontWhatsAppContact> {
    return this.http.get<StorefrontWhatsAppContact>(`${this.base}/content/whatsapp-contact`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getChatbotWidget(companyId: number): Observable<StorefrontChatbotWidget> {
    return this.http.get<StorefrontChatbotWidget>(`${this.base}/chatbot/widget`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  askChatbot(
    companyId: number,
    message: string,
    options?: {
      contextProductId?: number | null;
      sessionToken?: string | null;
      visitorEmail?: string | null;
      visitorName?: string | null;
      visitorPhone?: string | null;
    },
  ): Observable<StorefrontChatbotAskResponse> {
    const body: Record<string, unknown> = { message };
    if (options?.contextProductId != null) body['contextProductId'] = options.contextProductId;
    if (options?.sessionToken) body['sessionToken'] = options.sessionToken;
    if (options?.visitorEmail) body['visitorEmail'] = options.visitorEmail;
    if (options?.visitorName) body['visitorName'] = options.visitorName;
    if (options?.visitorPhone) body['visitorPhone'] = options.visitorPhone;
    return this.http.post<StorefrontChatbotAskResponse>(
      `${this.base}/chatbot/ask`,
      body,
      { params: new HttpParams().set('companyId', String(companyId)) },
    );
  }

  getChatbotHistory(companyId: number, sessionToken: string): Observable<StorefrontChatbotHistoryMessage[]> {
    return this.http.get<StorefrontChatbotHistoryMessage[]>(`${this.base}/chatbot/history`, {
      params: new HttpParams().set('companyId', String(companyId)).set('sessionToken', sessionToken),
    });
  }

  listOrders(companyId: number): Observable<StorefrontOrderSummary[]> {
    return this.http.get<StorefrontOrderSummary[]>(`${this.base}/account/orders`, {
      headers: this.authHeaders(companyId),
    });
  }

  /** #FUNCIONALIDAD Detalle de un pedido del comprador (productos, montos, pago pendiente, entrega y devoluciones). */
  getOrder(companyId: number, orderId: number): Observable<StorefrontOrderDetail> {
    return this.http.get<StorefrontOrderDetail>(`${this.base}/account/orders/${orderId}`, {
      headers: this.authHeaders(companyId),
    });
  }

  /** #FUNCIONALIDAD Solicitar la devolución de productos de un pedido entregado (la tienda la revisa). */
  requestReturn(companyId: number, orderId: number, body: StorefrontReturnRequest): Observable<StorefrontOrderReturn> {
    return this.http.post<StorefrontOrderReturn>(`${this.base}/account/orders/${orderId}/returns`, body, {
      headers: this.authHeaders(companyId),
    });
  }

  getHeaderMenu(companyId: number): Observable<StorefrontMenuItem[]> {
    return this.http.get<StorefrontMenuItem[]>(`${this.base}/content/menu`, {
      params: new HttpParams().set('companyId', String(companyId)).set('location', 'HEADER'),
    });
  }

  getFooterMenu(companyId: number): Observable<StorefrontMenuItem[]> {
    return this.http.get<StorefrontMenuItem[]>(`${this.base}/content/menu`, {
      params: new HttpParams().set('companyId', String(companyId)).set('location', 'FOOTER'),
    });
  }

  getActiveTheme(companyId: number, themePreviewId?: number | null): Observable<StorefrontTheme> {
    let params = new HttpParams().set('companyId', String(companyId));
    if (themePreviewId != null) params = params.set('themePreview', String(themePreviewId));
    return this.http.get<StorefrontTheme>(`${this.base}/content/theme`, { params });
  }

  /** Alta en el boletín desde el formulario de la tienda (público, sin sesión). */
  subscribeNewsletter(companyId: number, email: string): Observable<unknown> {
    return this.http.post(`${this.base}/newsletter/subscribe`, { email }, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getHomeBanners(companyId: number): Observable<StorefrontHomeBanners> {
    return this.http.get<StorefrontHomeBanners>(`${this.base}/content/banners`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  /** Envía el formulario «Contacto» al correo de la tienda (público, sin sesión). */
  sendContactMessage(companyId: number, body: StorefrontContactMessage): Observable<void> {
    return this.http.post<void>(`${this.base}/content/contact`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getPage(companyId: number, slug: string): Observable<StorefrontPageDetail> {
    return this.http.get<StorefrontPageDetail>(`${this.base}/content/pages/${slug}`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  listBlogPosts(
    companyId: number,
    filters?: { categorySlug?: string; tagSlug?: string },
  ): Observable<StorefrontBlogPostSummary[]> {
    let params = new HttpParams().set('companyId', String(companyId));
    if (filters?.categorySlug?.trim()) {
      params = params.set('categorySlug', filters.categorySlug.trim());
    }
    if (filters?.tagSlug?.trim()) {
      params = params.set('tagSlug', filters.tagSlug.trim());
    }
    return this.http.get<StorefrontBlogPostSummary[]>(`${this.base}/content/blog`, { params });
  }

  getBlogPost(companyId: number, slug: string): Observable<StorefrontBlogPostDetail> {
    return this.http.get<StorefrontBlogPostDetail>(`${this.base}/content/blog/${slug}`, {
      params: new HttpParams().set('companyId', String(companyId)),
    });
  }

  getBlogPreview(companyId: number, postId: number, token: string): Observable<StorefrontBlogPostDetail> {
    return this.http.get<StorefrontBlogPostDetail>(`${this.base}/content/blog/preview`, {
      params: new HttpParams()
        .set('companyId', String(companyId))
        .set('postId', String(postId))
        .set('token', token),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  createBlogComment(
    companyId: number,
    slug: string,
    body: { authorName: string; email: string; body: string },
  ): Observable<StorefrontBlogCommentSubmission> {
    return this.http.post<StorefrontBlogCommentSubmission>(`${this.base}/content/blog/${slug}/comments`, body, {
      params: new HttpParams().set('companyId', String(companyId)),
      context: new HttpContext().set(SUPPRESS_HTTP_ERRORS, true),
    });
  }

  private cartHeaders(companyId: number): HttpHeaders {
    let h = new HttpHeaders();
    const token = this.session.getCartSession(companyId);
    if (token) h = h.set('X-Cart-Session', token);
    const auth = this.session.getAuth(companyId);
    if (auth) h = h.set('Authorization', `Bearer ${auth.accessToken}`);
    return h;
  }

  private syncCart(companyId: number, cart: StorefrontCart): void {
    if (cart.sessionToken) {
      this.session.setCartSession(companyId, cart.sessionToken);
    }
    // Preferir longitud real de ítems: itemCount en BD puede quedar desfasado
    const count = Array.isArray(cart.items) ? cart.items.length : (cart.itemCount ?? 0);
    this.cartState.setCount(companyId, count);
  }

  private authHeaders(companyId: number): HttpHeaders {
    const auth = this.session.getAuth(companyId);
    return new HttpHeaders(auth ? { Authorization: `Bearer ${auth.accessToken}` } : {});
  }
}
